// dependencies
import React, { Children, PropsWithChildren, useMemo } from "react";

// Styles
import css from "./Grid.module.scss";

interface GridProps {
	/**
	 * Additonal classes to add to the grid.
	 */
	className?: string;
	/**
	 * Distance between grid items. (Numbers are multiples of 8px.
	 * Ex: 1 = 8px, 2 = 16px, 3 = 24px, etc.)
	 * @default 4
	 */
	gap?: 0 | 1 | 2 | 3 | 4 | 5;
	/**
	 * Number of columns in the grid. (Numbers are multiples of 8px.
	 * Ex: 1 = 8px, 2 = 16px, 3 = 24px, etc.)
	 * @default 1
	 */
	cols?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
	/**
	 * Padding off left and right sides of grid. (Numbers are multiples of 8px.
	 * Ex: 1 = 8px, 2 = 16px, 3 = 24px, etc.)
	 */
	padding?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
}

/**
 * Grid component for laying out content in a grid format.
 */
export const Grid = (props: PropsWithChildren<GridProps>) => {
	const { cols = 1, gap = 4, children, padding = 0, className = "" } = props;

	const columnWidths = Children.toArray(children).map((child: any) => {
		return child.props.width;
	});

	const allWidthsDefined = columnWidths.every((width) => width !== undefined);
	const atLeastOneWidthDefined = columnWidths.some((width) => width !== undefined);

	if (!allWidthsDefined && atLeastOneWidthDefined) {
		console.warn("Grid: You must either define a size for all columns or none at all.");
	}

	const gridTemplateColumns = useMemo(() => {
		if (allWidthsDefined) {
			return columnWidths
				.map((width) => {
					const widthStr = width.toString();
					const hasUnit = widthStr.includes("px") || widthStr.includes("%") || widthStr.includes("fr");
					return hasUnit ? widthStr : `${widthStr}px`;
				})
				.join(" ");
		}

		return undefined;
	}, [columnWidths, allWidthsDefined]);

	return (
		<div
			style={{
				gridTemplateColumns,
			}}
			className={` ${css.grid} ${css[`grid-padding-${padding}`]} ${css[`grid-cols-${cols}`]} ${css[`grid-gap-${gap}`]} ${className} `}
		>
			{children}
		</div>
	);
};

interface GridColProps {
	/**
	 * Number of columns to span. (Numbers are multiples of 8px.
	 * Ex: 1 = 8px, 2 = 16px, 3 = 24px, etc.)
	 * @default 1
	 */
	span?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

	/**
	 * Allows you to set a custom width for the column.
	 */
	width?: string | number;

	/**
	 * Additonal classes to add to the grid.
	 */
	className?: string;
	/**
	 * testID, can be used for testing
	 */
	testID?: string;
}

/**
 * Used as a child of a <Grid /> component to define the bounds of a column.
 */
export const GridCol = (props: PropsWithChildren<GridColProps>) => {
	const { span = 1, children, className = "", testID = "" } = props;

	return (
		<div className={`${css[`grid-span-${span}`]} ${className}`} data-testid={testID}>
			{children}
		</div>
	);
};
